<template>
    <div class="flex-column">
        <div class="settings-template container-big flex flex-column ov-reset self-center wp-10">
            <div class="header mb-3">
                <h1 class="mb-1">{{ $l10n.__('Réglages') }}</h1>
                <CompassBackForwardButton
                    :label="$t.__('settings.SettingsTemplate.back_settings')"
                    :link="backUrl"
                    back
                />
            </div>
            <div class="flex-row hp-10 body">
                <div v-if="isCustomMenu" class="settings-nav clearfix flex-column">
                    <slot name="menu">
                        <div
                            v-for="(item, key) in menuItems"
                            :key="key"
                            ref="subMenu"
                            :class="{ selected: isSelected(item) }"
                            class="wp-10 flex-row sub-menu jc-space ai-center p-2 cpointer"
                            @click="onMenuClick(item)"
                        >
                            <div class="typographies-paragraph-medium-bold sub-menu__text">
                                {{ item.label }}
                            </div>
                            <CompassIcon icon="caret-right" size="extra-small" type="primary" />
                        </div>
                    </slot>
                </div>
                <div v-else class="settings-nav clearfix flex-column">
                    <slot name="menu">
                        <div v-for="(item, key, index) in menu" :key="key" class="wp-10 cpointer">
                            <div
                                :id="key"
                                ref="subMenu"
                                class="sub-menu flex-row jc-space ai-center p-2"
                                @click="onMenuClick(item, index)"
                            >
                                <div class="typographies-paragraph-medium-bold gray-80">
                                    {{ item.label }}
                                </div>
                                <CompassIcon icon="caret-right" size="extra-small" type="primary" />
                            </div>
                            <div class="sub-menu-content flex-column">
                                <div
                                    v-for="(subItem, subKey) in item.sub"
                                    :key="subKey"
                                    :class="isSelected(subItem) ? 'selected' : ''"
                                    class="sub-menu-content-item cpointer"
                                    @click="onSubMenuClick(key, subItem)"
                                >
                                    <div class="typographies-paragraph-medium-regular">
                                        {{ subItem.label }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </slot>
                </div>
                <router-view class="content"></router-view>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        backUrl: {
            type: String,
            required: false,
            default: '/settings',
        },
        /**
         * Exemple du template pour créer le menu de navigation
         * routeName : Pour la gestion du router
         * action: Si on a besoin d'appeler une action legacy, par exemple ici on appel la popup de config de Yousign
         *  {
         *      label: this.$l10n.__("Service de signature électronique"),
         *      routeName: 'settings.esign.providers',
         * },
         * {
         *      label: this.$l10n.__("Préférences signature électronique"),
         *      action: () => window.YousignPrefs.loadPrefs(),
         *      routeName: '',
         * },
         */
        menuItems: {
            type: Array,
            required: false,
            default: () => [],
        },
        isCustomMenu: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            menu: null,
            itemSelected: null,
        };
    },
    async created() {
        if (this.isCustomMenu) {
            this.menu = this.menuItems;
        } else {
            await this.$ajax.post('/?_f=listingPreferences&action=getSettings', {}, {}, (data) => {
                this.menu = data.settings;
                for (const [key, item] of Object.entries(this.menu)) {
                    this.itemSelected =
                        item?.sub.filter((subItem) => subItem.url === this.$route.path)[0] ?? null;
                    if (this.itemSelected) {
                        this.$nextTick(() => {
                            this.onMenuClick(
                                this.itemSelected,
                                Object.keys(this.menu).indexOf(key),
                            );
                        });
                        return;
                    }
                }
            });
        }
    },
    methods: {
        onSubMenuClick(key, subItem) {
            if (subItem.url === this.$route?.path) {
                return;
            }
            this.closeAllMenuOpen(key);
            this.itemSelected = subItem;
            if (this.isSelected(subItem)) {
                subItem.vueRouting
                    ? this.$router.push({ path: subItem.url })
                    : Utils.relocate(subItem.url);
            }
        },
        closeAllMenuOpen(key) {
            const menus = this.$refs.subMenu;
            for (const menu of menus) {
                if (menu.id === key) {
                    continue;
                }
                menu.classList.remove('active');
                menu.nextElementSibling.style.maxHeight = null;
            }
        },
        onMenuClick(item, index = null) {
            if (this.isCustomMenu) {
                this.itemSelected = item;
                if (item.action) return item['action'].call();
                if (!this.isSelected(item)) this.$router.push({ name: item.routeName });
            } else {
                const itemHtml = this.$refs.subMenu[index];
                itemHtml.classList.toggle('active');
                const content = itemHtml.nextElementSibling;
                if (content.style.maxHeight) {
                    content.style.maxHeight = null;
                } else {
                    content.style.maxHeight = content.scrollHeight + 'px';
                }
            }
        },
        isSelected(item) {
            if (this.isCustomMenu) {
                return item.routeName === this.$route.name;
            }
            return item === this.itemSelected;
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~@sellsy/styleguide/src/_variables';

.settings-template {
    margin-bottom: 24px;
    .body {
        gap: 24px;
        align-items: flex-start;
    }

    .content {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        width: 100%;
        min-height: 0;
        padding: var(--spacing-4);
        background-color: $white;
        border: 1px solid $gray-40;
        border-radius: 4px;
    }

    .settings-nav {
        align-items: flex-start;
        align-self: baseline;
        background-color: $white;
        border: 1px solid $gray-40;
        border-radius: 4px;
        flex: none;
        padding: 16px 12px;
        width: 314px;

        .menu-link {
            justify-content: space-between;
            width: 100%;
        }

        .sub-menu {
            transition: background-color 100ms cubic-bezier(0, 0, 0.3, 1);

            .compass-icon {
                rotate: initial;
                transition: rotate 200ms cubic-bezier(0, 0, 0.3, 1);
            }

            &.active {
                .compass-icon {
                    rotate: 90deg;
                }
            }

            &:hover {
                background-color: $gray-0;
                border-radius: 4px;
            }
            &__text {
                color: var(--colors-component-text-navigation-default);
            }
        }

        .selected {
            background-color: $primary-0;
            border-radius: 4px;

            .sub-menu__text {
                color: $primary-50;
                font-weight: $font-weight-semibold;
            }

            &:hover {
                background-color: $primary-05;
            }
        }

        .sub-menu-content {
            background-color: white;
            max-height: 0;
            overflow: hidden;
            transition: max-height 200ms cubic-bezier(0, 0, 0.3, 1);

            .sub-menu-content-item {
                padding: 8px 20px;
                transition: background-color 100ms cubic-bezier(0, 0, 0.3, 1);

                &:not(.selected) > .sub-menu__text {
                    color: $gray-80;
                }
                &:hover:not(.selected) {
                    background-color: $gray-0;
                    border-radius: 4px;
                }
            }
        }
    }
}
</style>
